import { RoutePending } from "@repo/app-shell"
import { authClient } from "@repo/db"
import {
  StatsigProvider as ReactBindingsStatsigProvider,
  useClientAsyncInit,
  useStatsigClient,
  useStatsigUser,
  type StatsigUser,
} from "@statsig/react-bindings"
import { createContext, useCallback, useContext, useMemo, useRef, type ReactNode } from "react"

import { buildBootstrapStatsigUser } from "@/features/statsig/user"

type TowerStatsigContextValue = {
  checkGate: (gateName: string) => boolean
  configured: boolean
  isLoading: boolean
  syncUserAsync: (user: StatsigUser) => Promise<void>
}

const noopAsync = async () => {}

const unconfiguredValue: TowerStatsigContextValue = {
  checkGate: () => false,
  configured: false,
  isLoading: false,
  syncUserAsync: noopAsync,
}

const TowerStatsigContext = createContext<TowerStatsigContextValue>(unconfiguredValue)

export function StatsigProvider({ children }: { children: ReactNode }) {
  const sdkKey = import.meta.env.VITE_STATSIG_CLIENT_KEY?.trim()
  const statsigEnabled = import.meta.env.PROD && !!sdkKey

  if (!statsigEnabled) {
    return (
      <TowerStatsigContext.Provider value={unconfiguredValue}>
        {children}
      </TowerStatsigContext.Provider>
    )
  }

  return <ConfiguredStatsigProvider sdkKey={sdkKey}>{children}</ConfiguredStatsigProvider>
}

function ConfiguredStatsigProvider({ children, sdkKey }: { children: ReactNode; sdkKey: string }) {
  const { data: session } = authClient.useSession()
  const { data: activeOrganization } = authClient.useActiveOrganization()
  const initialUserRef = useRef<StatsigUser | null>(null)

  if (initialUserRef.current === null) {
    initialUserRef.current = buildBootstrapStatsigUser({
      activeOrganization,
      sessionUser: session?.user,
    })
  }

  const { client } = useClientAsyncInit(sdkKey, initialUserRef.current)

  return (
    <ReactBindingsStatsigProvider client={client} loadingComponent={<RoutePending />}>
      <StatsigBridge>{children}</StatsigBridge>
    </ReactBindingsStatsigProvider>
  )
}

function StatsigBridge({ children }: { children: ReactNode }) {
  const { checkGate, isLoading } = useStatsigClient()
  const { updateUserAsync } = useStatsigUser()

  const syncUserAsync = useCallback(
    async (user: StatsigUser) => {
      await updateUserAsync(user)
    },
    [updateUserAsync],
  )

  const value = useMemo<TowerStatsigContextValue>(
    () => ({
      checkGate,
      configured: true,
      isLoading,
      syncUserAsync,
    }),
    [checkGate, isLoading, syncUserAsync],
  )

  return <TowerStatsigContext.Provider value={value}>{children}</TowerStatsigContext.Provider>
}

export function useTowerStatsig() {
  return useContext(TowerStatsigContext)
}
